import axios from 'axios';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.citycourier.org/api/v1';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// TypeScript Interfaces
export interface Rider {
  id: number;
  name: string;
  loginID: string;
}

export interface MasterUnit {
  id: number;
  mu_number: string;
  status_id: number;
  status: {
    id: number;
    name: string;
    badge: string;
  };
  client?: {
    id: number;
    name: string;
  };
  consignments?: any[];
}

export interface RunSheet {
  id: number;
  unique_id: string;
  rider_id: number;
  rider: Rider;
  branch_id: number;
  status_id: number;
  status: {
    id: number;
    name: string;
    badge: string;
  };
  created_at: string;
  updated_at: string;
  dispatched_at?: string;
  checked_in_at?: string;
  masterunits?: MasterUnit[];
  masterunit_count?: number;
}

export interface RunSheetFilters {
  status_id?: number | string;
  rider_id?: number | string;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}

export interface CreateRunSheetRequest {
  rider_id: number;
  masterunit_ids: number[];
}

export interface UpdateMasterUnitStatusRequest {
  status_id: number;
}

// API Functions

/**
 * Get all run sheets with filters
 */
export async function getRunSheets(filters?: RunSheetFilters) {
  try {
    const response = await apiClient.get('/run-sheets', { params: filters });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch run sheets');
  }
}

/**
 * Get single run sheet
 */
export async function getRunSheet(id: number) {
  try {
    const response = await apiClient.get(`/run-sheets/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch run sheet');
  }
}

/**
 * Create run sheet
 */
export async function createRunSheet(data: CreateRunSheetRequest) {
  try {
    const response = await apiClient.post('/run-sheets', data);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to create run sheet');
  }
}

/**
 * Dispatch run sheet
 */
export async function dispatchRunSheet(id: number) {
  try {
    const response = await apiClient.post(`/run-sheets/${id}/dispatch`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to dispatch run sheet');
  }
}

/**
 * Check-in run sheet
 */
export async function checkInRunSheet(id: number) {
  try {
    const response = await apiClient.post(`/run-sheets/${id}/check-in`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to check-in run sheet');
  }
}

/**
 * Update master unit status in run sheet
 */
export async function updateMasterUnitStatus(
  runSheetId: number,
  masterUnitId: number,
  data: UpdateMasterUnitStatusRequest
) {
  try {
    const response = await apiClient.put(
      `/run-sheets/${runSheetId}/master-units/${masterUnitId}/status`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update master unit status');
  }
}

/**
 * Get available master units for run sheet creation
 */
export async function getAvailableMasterUnits() {
  try {
    const response = await apiClient.get('/run-sheets/available-master-units');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch available master units');
  }
}

/**
 * Get riders for dropdown
 */
export async function getRiders() {
  try {
    const response = await apiClient.get('/run-sheets/riders');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch riders');
  }
}

/**
 * Print run sheet (opens PDF in new tab)
 */
export function printRunSheet(id: number) {
  const publicUrl = `${API_BASE_URL}/public/run-sheets/${id}/print`;
  window.open(publicUrl, '_blank', 'noopener,noreferrer');
}
